import { useState, useEffect, useCallback } from 'react';
import doctorApiService from '../services/doctorApiService';
import type {
  Doctor,
  DoctorFormData,
  DoctorSearchParams,
  Gender,
  DoctorStatus,
  ShiftPriority
} from '../types/doctor';

/**
 * 医師一覧管理用の状態型定義
 */
interface DoctorListState {
  /** 医師データ配列 */
  doctors: Doctor[];
  
  /** ページネーションメタ情報 */
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  } | null;
  
  /** ローディング状態 */
  isLoading: boolean;
  
  /** エラー情報 */
  error: string | null;
  
  /** 検索・フィルタリングパラメータ */
  searchParams: DoctorSearchParams;
  
  /** 選択された医師 */
  selectedDoctor: Doctor | null;
  
  /** 詳細モーダル表示状態 */
  detailModalOpen: boolean;
  
  /** 編集モーダル表示状態 */
  editModalOpen: boolean;
  
  /** 登録モーダル表示状態 */
  registerModalOpen: boolean;
  
  /** 削除確認ダイアログ表示状態 */
  deleteDialogOpen: boolean;
}

/**
 * 医師一覧管理用カスタムフック
 * 
 * 意図：
 * - 医師データの一覧取得・CRUD操作を統合管理
 * - 検索・フィルタリング機能の提供
 * - モーダル・ダイアログの状態管理
 * 
 * 注意点：
 * - APIエラー時の適切なエラーハンドリング
 * - メモリリークの防止（コンポーネントアンマウント時のクリーンアップ）
 * - 不要な再レンダリングの抑制
 * 
 * 改善提案：
 * - キャッシュ機能の実装
 * - オプティミスティックアップデート
 * - 無限スクロール対応
 */
export const useDoctorList = () => {
  const [state, setState] = useState<DoctorListState>({
    doctors: [],
    meta: null,
    isLoading: false,
    error: null,
    searchParams: {
      page: 1,
      per_page: 10
    },
    selectedDoctor: null,
    detailModalOpen: false,
    editModalOpen: false,
    registerModalOpen: false,
    deleteDialogOpen: false
  });

  /**
   * 医師一覧を取得
   */
  const fetchDoctors = useCallback(async (params?: DoctorSearchParams) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await doctorApiService.getDoctors(params || state.searchParams);
      setState(prev => ({
        ...prev,
        doctors: response.data,
        meta: response.meta || null,
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '医師一覧の取得に失敗しました',
        isLoading: false
      }));
    }
  }, [state.searchParams]);

  /**
   * 検索パラメータを更新
   */
  const updateSearchParams = useCallback((params: Partial<DoctorSearchParams>) => {
    setState(prev => ({
      ...prev,
      searchParams: {
        ...prev.searchParams,
        ...params,
        // ページ番号は検索条件変更時にリセット
        page: params.search !== undefined || params.status !== undefined || 
               params.gender !== undefined || params.shift_priority !== undefined 
               ? 1 : (params.page || prev.searchParams.page)
      }
    }));
  }, []);

  /**
   * 医師詳細情報を取得
   */
  const fetchDoctorDetail = useCallback(async (id: number) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await doctorApiService.getDoctor(id);
      setState(prev => ({
        ...prev,
        selectedDoctor: response.data,
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '医師詳細の取得に失敗しました',
        isLoading: false
      }));
    }
  }, []);

  /**
   * 医師を新規登録
   */
  const createDoctor = useCallback(async (data: DoctorFormData) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await doctorApiService.createDoctor(data);
      setState(prev => ({ ...prev, isLoading: false, registerModalOpen: false }));
      // 一覧を再取得
      await fetchDoctors();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '医師の登録に失敗しました',
        isLoading: false
      }));
      throw error;
    }
  }, [fetchDoctors]);

  /**
   * 医師情報を更新
   */
  const updateDoctor = useCallback(async (id: number, data: DoctorFormData) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await doctorApiService.updateDoctor(id, data);
      setState(prev => ({ ...prev, isLoading: false, editModalOpen: false }));
      // 一覧を再取得
      await fetchDoctors();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '医師情報の更新に失敗しました',
        isLoading: false
      }));
      throw error;
    }
  }, [fetchDoctors]);

  /**
   * 医師を削除
   */
  const deleteDoctor = useCallback(async (id: number) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await doctorApiService.deleteDoctor(id);
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        deleteDialogOpen: false,
        selectedDoctor: null 
      }));
      // 一覧を再取得
      await fetchDoctors();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : '医師の削除に失敗しました',
        isLoading: false
      }));
      throw error;
    }
  }, [fetchDoctors]);

  /**
   * 詳細モーダルを開く
   * 一覧データで即座に表示し、詳細APIで完全なデータを取得
   */
  const openDetailModal = useCallback(async (doctor: Doctor) => {
    // まず一覧データで表示（即時レスポンス）
    setState(prev => ({
      ...prev,
      selectedDoctor: doctor,
      detailModalOpen: true,
      isLoading: true
    }));

    try {
      // 詳細APIを呼び出して完全なデータを取得（medical_spreadsheet_urlを含む）
      const response = await doctorApiService.getDoctor(doctor.id);
      setState(prev => ({
        ...prev,
        selectedDoctor: response.data,
        isLoading: false
      }));
    } catch (error) {
      console.error('医師詳細の取得に失敗しました:', error);
      setState(prev => ({
        ...prev,
        isLoading: false
      }));
    }
  }, []);

  /**
   * 編集モーダルを開く
   */
  const openEditModal = useCallback((doctor: Doctor) => {
    setState(prev => ({
      ...prev,
      selectedDoctor: doctor,
      editModalOpen: true
    }));
  }, []);

  /**
   * 登録モーダルを開く
   */
  const openRegisterModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedDoctor: null,
      registerModalOpen: true
    }));
  }, []);

  /**
   * 削除確認ダイアログを開く
   */
  const openDeleteDialog = useCallback((doctor: Doctor) => {
    setState(prev => ({
      ...prev,
      selectedDoctor: doctor,
      deleteDialogOpen: true
    }));
  }, []);

  /**
   * モーダル・ダイアログを閉じる
   */
  const closeModals = useCallback(() => {
    setState(prev => ({
      ...prev,
      detailModalOpen: false,
      editModalOpen: false,
      registerModalOpen: false,
      deleteDialogOpen: false,
      selectedDoctor: null
    }));
  }, []);

  /**
   * 初回マウント時に医師一覧を取得
   */
  useEffect(() => {
    fetchDoctors();
  }, [state.searchParams]);

  return {
    // 状態
    doctors: state.doctors,
    meta: state.meta,
    isLoading: state.isLoading,
    error: state.error,
    searchParams: state.searchParams,
    selectedDoctor: state.selectedDoctor,
    
    // モーダル状態
    detailModalOpen: state.detailModalOpen,
    editModalOpen: state.editModalOpen,
    registerModalOpen: state.registerModalOpen,
    deleteDialogOpen: state.deleteDialogOpen,
    
    // アクション
    fetchDoctors,
    fetchDoctorDetail,
    createDoctor,
    updateDoctor,
    deleteDoctor,
    updateSearchParams,
    
    // モーダル操作
    openDetailModal,
    openEditModal,
    openRegisterModal,
    openDeleteDialog,
    closeModals
  };
};